// ABOUTME: Admin modal for comprehensive subscription management with inline action forms
// ABOUTME: Provides free access management, admin actions, notes, and paginated history

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  Grid,
  Divider,
  Chip,
  Collapse,
  CircularProgress,
  TablePagination,
  IconButton
} from '@mui/material';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import NoteIcon from '@mui/icons-material/Note';
import WebhookIcon from '@mui/icons-material/Webhook';
import CloseIcon from '@mui/icons-material/Close';
import { formatDistanceToNow, format, differenceInDays, parseISO } from 'date-fns';
import {
  SubscriptionInfo,
  SubscriptionHistoryEntry,
} from '../types';
import SubscriptionStatusBanner from '../SubscriptionStatusBanner';
import api from '../../../services/api';
import { adminAPI } from '../../../services/api';

const INACTIVE_LS_STATES = ['cancelled', 'expired', 'past_due', 'paused', 'unpaid', 'refunded'];
const isInactiveLsState = (status: string) => INACTIVE_LS_STATES.includes(status.toLowerCase());

interface ManageSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  familyId: number;
  familyName: string;
  subscriptionInfo: SubscriptionInfo | null;
  onActionComplete: () => void;
}

// Helper to format dates nicely
function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return 'Invalid Date';
  }
}

function formatTimestamp(dateString: string): string {
  try {
    const date = parseISO(dateString);
    return `${format(date, 'MMM d, yyyy h:mm a')} (${formatDistanceToNow(date, { addSuffix: true })})`;
  } catch {
    return dateString;
  }
}

function getTrialDaysRemaining(trialEndsAt: string | undefined): number | null {
  if (!trialEndsAt) return null;
  try {
    const days = differenceInDays(parseISO(trialEndsAt), new Date());
    return Math.max(0, days);
  } catch {
    return null;
  }
}

const ManageSubscriptionModal: React.FC<ManageSubscriptionModalProps> = ({
  open,
  onClose,
  familyId,
  familyName,
  subscriptionInfo,
  onActionComplete
}) => {
  // Action form state - only one action form open at a time
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Free access form state
  const [freeAccessReason, setFreeAccessReason] = useState('');
  const [freeAccessExpiresAt, setFreeAccessExpiresAt] = useState<string>('');

  // Revoke free access form state
  const [revokeReason, setRevokeReason] = useState('');

  // Extend trial form state
  const [extendTrialDays, setExtendTrialDays] = useState<number>(14);
  const [extendTrialReason, setExtendTrialReason] = useState('');

  // Restart trial form state
  const [restartTrialReason, setRestartTrialReason] = useState('');

  // Force expire form state
  const [forceExpireReason, setForceExpireReason] = useState('');

  // Emergency lockout form state
  const [lockoutReason, setLockoutReason] = useState('');

  // Remove lockout form state
  const [removeLockoutReason, setRemoveLockoutReason] = useState('');

  // Note form state
  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  // History state
  const [history, setHistory] = useState<SubscriptionHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await adminAPI.getSubscriptionHistory(familyId, historyPage + 1, historyPageSize);
      setHistory(data.data || []);
      setHistoryTotalCount(data.totalCount || 0);
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setHistoryError(message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [familyId, historyPage, historyPageSize]);

  // Fetch history when modal opens or page changes
  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, fetchHistory]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setActiveAction(null);
      setActionError(null);
      setActionLoading(false);
      setFreeAccessReason('');
      setFreeAccessExpiresAt('');
      setRevokeReason('');
      setExtendTrialDays(14);
      setExtendTrialReason('');
      setRestartTrialReason('');
      setForceExpireReason('');
      setLockoutReason('');
      setRemoveLockoutReason('');
      setNoteText('');
      setHistoryPage(0);
    }
  }, [open]);

  const openAction = (action: string) => {
    setActiveAction(action);
    setActionError(null);
  };

  const closeAction = () => {
    setActiveAction(null);
    setActionError(null);
  };

  const handleActionSuccess = () => {
    closeAction();
    onActionComplete();
    fetchHistory();
  };

  // Pre-fill edit form when opening edit free access
  useEffect(() => {
    if (activeAction === 'editFreeAccess' && subscriptionInfo) {
      setFreeAccessReason(subscriptionInfo.freeAccountReason || '');
      setFreeAccessExpiresAt(subscriptionInfo.freeAccountExpiresAt || '');
    }
  }, [activeAction, subscriptionInfo]);

  // --- Action handlers ---

  const handleGrantFreeAccess = async () => {
    if (!freeAccessReason || freeAccessReason.length < 3 || !freeAccessExpiresAt) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.post(`/api/admin/subscriptions/${familyId}/grant-free`, {
        reason: freeAccessReason,
        expiresAt: new Date(freeAccessExpiresAt).toISOString()
      });
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to grant free access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditFreeAccess = async () => {
    if (!freeAccessReason || freeAccessReason.length < 3 || !freeAccessExpiresAt) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminAPI.editFreeAccess(familyId, freeAccessReason, new Date(freeAccessExpiresAt).toISOString());
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to edit free access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeFreeAccess = async () => {
    if (!revokeReason || revokeReason.length < 3) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.post(`/api/admin/subscriptions/${familyId}/revoke-free`, {
        reason: revokeReason
      });
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to revoke free access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!extendTrialReason || extendTrialReason.length < 3 || extendTrialDays < 1) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminAPI.extendTrial(familyId, extendTrialDays, extendTrialReason);
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to extend trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestartTrial = async () => {
    if (!restartTrialReason || restartTrialReason.length < 3) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminAPI.restartTrial(familyId, restartTrialReason);
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to restart trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceExpire = async () => {
    if (!forceExpireReason || forceExpireReason.length < 3) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminAPI.forceExpire(familyId, forceExpireReason);
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to force expire');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmergencyLockout = async () => {
    if (!lockoutReason || lockoutReason.length < 3) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminAPI.emergencyLockout(familyId, lockoutReason);
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to lock out family');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveLockout = async () => {
    if (!removeLockoutReason || removeLockoutReason.length < 3) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminAPI.removeLockout(familyId, removeLockoutReason);
      handleActionSuccess();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to remove lockout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || noteText.trim().length < 3) return;
    setNoteLoading(true);
    try {
      await adminAPI.addSubscriptionNote(familyId, noteText.trim());
      setNoteText('');
      fetchHistory();
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setActionError(message || 'Failed to add note');
    } finally {
      setNoteLoading(false);
    }
  };

  const handleDateChange = (date: unknown) => {
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      setFreeAccessExpiresAt(date.toISOString());
    } else {
      setFreeAccessExpiresAt('');
    }
  };

  if (!subscriptionInfo) return null;

  const status = subscriptionInfo.status.toLowerCase();
  const trialDaysRemaining = getTrialDaysRemaining(subscriptionInfo.trialEndsAt);

  // --- Render helpers ---

  const renderFreeAccessForm = (isEdit: boolean) => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2">
          {isEdit ? 'Edit Free Access' : 'Grant Free Access'}
        </Typography>
        <TextField
          label="Reason"
          fullWidth
          required
          multiline
          rows={2}
          value={freeAccessReason}
          onChange={(e) => setFreeAccessReason(e.target.value)}
          placeholder="e.g., Pilot user, Special case, etc."
          helperText="Minimum 3 characters"
        />
        <DatePicker
          label="Expiration Date"
          value={freeAccessExpiresAt ? new Date(freeAccessExpiresAt) : null}
          onChange={handleDateChange}
          minDate={new Date()}
          slotProps={{
            textField: {
              required: true,
              fullWidth: true,
              helperText: 'Free access will expire at end of this date'
            }
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={closeAction} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={isEdit ? handleEditFreeAccess : handleGrantFreeAccess}
            disabled={actionLoading || !freeAccessReason || freeAccessReason.length < 3 || !freeAccessExpiresAt}
          >
            {actionLoading ? <CircularProgress size={20} /> : (isEdit ? 'Save Changes' : 'Grant Free Access')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );

  const renderReasonForm = (
    label: string,
    reason: string,
    setReason: (val: string) => void,
    onSubmit: () => void,
    submitLabel: string,
    color: 'primary' | 'warning' | 'error' = 'primary'
  ) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Typography variant="subtitle2">{label}</Typography>
      <TextField
        label="Reason"
        fullWidth
        required
        multiline
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        helperText="Minimum 3 characters"
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={closeAction} disabled={actionLoading}>Cancel</Button>
        <Button
          variant="contained"
          color={color}
          onClick={onSubmit}
          disabled={actionLoading || !reason || reason.length < 3}
        >
          {actionLoading ? <CircularProgress size={20} /> : submitLabel}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="span">Manage Subscription — {familyName}</Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {/* Section 1: Subscription Summary */}
        <SubscriptionStatusBanner subscriptionInfo={subscriptionInfo} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Status:</strong> {subscriptionInfo.status}
            </Typography>
          </Grid>

          {(subscriptionInfo.billingOwnerName || subscriptionInfo.billingOwnerEmail) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Billing Owner:</strong> {subscriptionInfo.billingOwnerName || 'N/A'}
                {subscriptionInfo.billingOwnerEmail && ` (${subscriptionInfo.billingOwnerEmail})`}
              </Typography>
            </Grid>
          )}

          {subscriptionInfo.trialStartsAt && subscriptionInfo.trialEndsAt && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Trial:</strong> {formatDate(subscriptionInfo.trialStartsAt)} — {formatDate(subscriptionInfo.trialEndsAt)}
                {trialDaysRemaining !== null && status === 'trial' && ` (${trialDaysRemaining} days remaining)`}
              </Typography>
            </Grid>
          )}

          {subscriptionInfo.currentPeriodStart && subscriptionInfo.currentPeriodEnd && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Period:</strong> {formatDate(subscriptionInfo.currentPeriodStart)} — {formatDate(subscriptionInfo.currentPeriodEnd)}
              </Typography>
            </Grid>
          )}

          {subscriptionInfo.lemonSqueezySubscriptionId && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>LS Subscription ID:</strong> {subscriptionInfo.lemonSqueezySubscriptionId}
              </Typography>
            </Grid>
          )}

          {subscriptionInfo.isFreeAccount && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Free Access Reason:</strong> {subscriptionInfo.freeAccountReason || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Granted By:</strong> {subscriptionInfo.freeAccountGrantedBy || 'N/A'}
                  {subscriptionInfo.freeAccountGrantedAt && ` on ${formatDate(subscriptionInfo.freeAccountGrantedAt)}`}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Free Access Expires:</strong> {formatDate(subscriptionInfo.freeAccountExpiresAt)}
                </Typography>
              </Grid>
            </>
          )}

        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Section 2: Free Access Management */}
        <Typography variant="h6" sx={{ mb: 2 }}>Free Access Management</Typography>

        {status === 'active' && !subscriptionInfo.isFreeAccount ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Cannot grant free access to an active subscriber.
          </Alert>
        ) : status === 'free' || subscriptionInfo.isFreeAccount ? (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => openAction('editFreeAccess')}
                disabled={activeAction !== null && activeAction !== 'editFreeAccess'}
              >
                Edit Free Access
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={() => openAction('revokeFreeAccess')}
                disabled={activeAction !== null && activeAction !== 'revokeFreeAccess'}
              >
                Revoke Free Access
              </Button>
            </Box>
            <Collapse in={activeAction === 'editFreeAccess'}>
              {renderFreeAccessForm(true)}
            </Collapse>
            <Collapse in={activeAction === 'revokeFreeAccess'}>
              {renderReasonForm(
                'Revoke Free Access',
                revokeReason,
                setRevokeReason,
                handleRevokeFreeAccess,
                'Revoke',
                'warning'
              )}
            </Collapse>
          </Box>
        ) : (status === 'trial' || isInactiveLsState(status)) ? (
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => openAction('grantFreeAccess')}
              disabled={activeAction !== null && activeAction !== 'grantFreeAccess'}
            >
              Grant Free Access
            </Button>
            <Collapse in={activeAction === 'grantFreeAccess'}>
              {renderFreeAccessForm(false)}
            </Collapse>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No free access actions available for this subscription state.
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Section 3: Admin Actions */}
        <Typography variant="h6" sx={{ mb: 2 }}>Admin Actions</Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {status === 'trial' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openAction('extendTrial')}
              disabled={activeAction !== null && activeAction !== 'extendTrial'}
            >
              Extend Trial
            </Button>
          )}

          {isInactiveLsState(status) && !subscriptionInfo.isFreeAccount && status !== 'trial' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openAction('restartTrial')}
              disabled={activeAction !== null && activeAction !== 'restartTrial'}
            >
              Restart Trial
            </Button>
          )}

          {(status === 'trial' || status === 'free' || subscriptionInfo.isFreeAccount) && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => openAction('forceExpire')}
              disabled={activeAction !== null && activeAction !== 'forceExpire'}
            >
              Force Expire
            </Button>
          )}

          {!subscriptionInfo.isLockedOut ? (
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => openAction('emergencyLockout')}
              disabled={activeAction !== null && activeAction !== 'emergencyLockout'}
            >
              Emergency Lockout
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openAction('removeLockout')}
              disabled={activeAction !== null && activeAction !== 'removeLockout'}
            >
              Remove Lockout
            </Button>
          )}
        </Box>

        <Collapse in={activeAction === 'extendTrial'}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2">Extend Trial</Typography>
            <TextField
              label="Days to Add"
              type="number"
              fullWidth
              required
              value={extendTrialDays}
              onChange={(e) => setExtendTrialDays(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: 365 }}
              helperText="Number of days to extend the trial"
            />
            <TextField
              label="Reason"
              fullWidth
              required
              multiline
              rows={2}
              value={extendTrialReason}
              onChange={(e) => setExtendTrialReason(e.target.value)}
              helperText="Minimum 3 characters"
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={closeAction} disabled={actionLoading}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleExtendTrial}
                disabled={actionLoading || !extendTrialReason || extendTrialReason.length < 3 || extendTrialDays < 1}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Extend Trial'}
              </Button>
            </Box>
          </Box>
        </Collapse>

        <Collapse in={activeAction === 'restartTrial'}>
          {renderReasonForm(
            'Restart Trial',
            restartTrialReason,
            setRestartTrialReason,
            handleRestartTrial,
            'Restart Trial'
          )}
        </Collapse>

        <Collapse in={activeAction === 'forceExpire'}>
          {renderReasonForm(
            'Force Expire Subscription',
            forceExpireReason,
            setForceExpireReason,
            handleForceExpire,
            'Force Expire',
            'warning'
          )}
        </Collapse>

        <Collapse in={activeAction === 'emergencyLockout'}>
          {renderReasonForm(
            'Emergency Lockout',
            lockoutReason,
            setLockoutReason,
            handleEmergencyLockout,
            'Lock Out Family',
            'error'
          )}
        </Collapse>

        <Collapse in={activeAction === 'removeLockout'}>
          {renderReasonForm(
            'Remove Lockout',
            removeLockoutReason,
            setRemoveLockoutReason,
            handleRemoveLockout,
            'Remove Lockout'
          )}
        </Collapse>

        <Divider sx={{ my: 3 }} />

        {/* Section 4: Notes & History */}
        <Typography variant="h6" sx={{ mb: 2 }}>Notes & History</Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'flex-start' }}>
          <TextField
            label="Add a note"
            fullWidth
            multiline
            rows={2}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add an admin note about this subscription..."
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={noteLoading || !noteText.trim() || noteText.trim().length < 3}
            sx={{ mt: 0.5, minWidth: 100 }}
          >
            {noteLoading ? <CircularProgress size={20} /> : 'Add Note'}
          </Button>
        </Box>

        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : historyError ? (
          <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No history entries yet.
          </Typography>
        ) : (
          <Box>
            {history.map((entry) => (
              <Box
                key={entry.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  py: 1.5,
                  px: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <Box sx={{ pt: 0.5 }}>
                  {entry.type === 'note' ? (
                    <NoteIcon color="primary" fontSize="small" />
                  ) : (
                    <WebhookIcon color="secondary" fontSize="small" />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {formatTimestamp(entry.timestamp)}
                    </Typography>
                    <Chip
                      label={entry.source}
                      size="small"
                      color={entry.type === 'note' ? 'primary' : 'secondary'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                    {entry.actor && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        by {entry.actor}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">{entry.description}</Typography>
                </Box>
              </Box>
            ))}
            <TablePagination
              component="div"
              count={historyTotalCount}
              page={historyPage}
              onPageChange={(_e, newPage) => setHistoryPage(newPage)}
              rowsPerPage={historyPageSize}
              onRowsPerPageChange={(e) => {
                setHistoryPageSize(parseInt(e.target.value, 10));
                setHistoryPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageSubscriptionModal;
